import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Users, BarChart3, Tag, CircleDot, Trash2, Search, Copy, Shield, Eye, UserPlus, ChevronDown, ChevronUp, Check, Send, Mail, Sparkles, RefreshCw, Rocket } from 'lucide-react';
import AppLogo from '../components/AppLogo';
import CreateCodeDialog from '../components/admin/CreateCodeDialog';
import UserActivityDialog from '../components/admin/UserActivityDialog';
import EmailUserDialog from '../components/admin/EmailUserDialog';
import EmailsPanel from '../components/admin/EmailsPanel';
import CircleInspectorDialog from '../components/admin/CircleInspectorDialog';
import BetaInquiriesPanel from '../components/admin/BetaInquiriesPanel';

const AdminPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [circles, setCircles] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [newCodeDialog, setNewCodeDialog] = useState(false);
  const [newCode, setNewCode] = useState({ code: '', plan: 'basic', max_uses: '' });
  const [createLoading, setCreateLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [showStranded, setShowStranded] = useState(false);
  const [emailUser, setEmailUser] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailCheck, setEmailCheck] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const handleEmailCheck = async () => {
    setCheckingEmail(true);
    setEmailCheck(null);
    try {
      const res = await axios.get(`${API}/admin/email-health`, { timeout: 40000 });
      setEmailCheck(res.data);
      if (res.data.test_send_status === 'sent') toast.success('Email system is working!');
      else toast.error(`Email problem: ${res.data.test_send_error || res.data.test_send_status}`);
    } catch (e) {
      setEmailCheck({ test_send_status: 'failed', test_send_error: e.response?.data?.detail || e.message });
      toast.error('Email health check failed');
    }
    setCheckingEmail(false);
  };

  const handleDeleteCircle = async (circleId, circleName) => {
    if (!window.confirm(`Delete circle "${circleName}" and ALL its data? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/circles/${circleId}`);
      toast.success(`Circle "${circleName}" deleted`);
      setCircles(circles.filter(c => c.id !== circleId));
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete circle'); }
  };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [statsRes, usersRes, circlesRes, codesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/circles`),
        axios.get(`${API}/admin/promo-codes`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setCircles(circlesRes.data);
      setPromoCodes(codesRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally { setLoading(false); }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success(`User ${email} deleted`);
      setUsers(users.filter(u => u.id !== userId));
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete'); }
  };

  const handleCreateCode = async () => {
    setCreateLoading(true);
    try {
      const body = { plan: newCode.plan };
      if (newCode.code.trim()) body.code = newCode.code.trim();
      if (newCode.max_uses) body.max_uses = parseInt(newCode.max_uses);
      const res = await axios.post(`${API}/admin/promo-codes`, body);
      toast.success(`Code ${res.data.code} created!`);
      setNewCodeDialog(false);
      setNewCode({ code: '', plan: 'basic', max_uses: '' });
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create code'); }
    finally { setCreateLoading(false); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => toast.success('Copied!')).catch(() => toast.error('Could not copy'));
  };

  const getPlanBadgeClass = (plan) => {
    if (plan === 'premium') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    if (plan === 'basic_plus') return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300';
    return 'bg-muted text-muted-foreground';
  };
  const getPlanLabel = (plan) => {
    if (plan === 'premium') return 'Coach';
    if (plan === 'basic_plus') return 'Basic+';
    return 'Basic';
  };

  const strandedCount = users.filter(u => u.circle_count === 0).length;

  const filteredUsers = users.filter(u =>
    (!searchQuery || u.email?.toLowerCase().includes(searchQuery.toLowerCase()) || u.name?.toLowerCase().includes(searchQuery.toLowerCase()))
    && (!showStranded || u.circle_count === 0)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="admin-page">
      <header className="surface-header sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 py-3.5 flex items-center gap-3">
          <AppLogo />
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-cyan-600" />
            <div>
              <h1 className="text-xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Admin Console</h1>
              <p className="text-xs text-muted-foreground">CircleUpSquad Management</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-6 max-w-3xl mb-6">
            <TabsTrigger value="overview"><BarChart3 className="w-4 h-4 mr-1.5" />Overview</TabsTrigger>
            <TabsTrigger value="users"><Users className="w-4 h-4 mr-1.5" />Users</TabsTrigger>
            <TabsTrigger value="circles"><CircleDot className="w-4 h-4 mr-1.5" />Circles</TabsTrigger>
            <TabsTrigger value="codes"><Tag className="w-4 h-4 mr-1.5" />Codes</TabsTrigger>
            <TabsTrigger value="emails" data-testid="emails-tab-trigger"><Mail className="w-4 h-4 mr-1.5" />Emails</TabsTrigger>
            <TabsTrigger value="beta" data-testid="beta-tab-trigger"><Rocket className="w-4 h-4 mr-1.5" />Beta &amp; Pilots</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Total Users', value: stats?.users || 0, color: 'text-cyan-600' },
                { label: 'Circles', value: stats?.circles || 0, color: 'text-emerald-600' },
                { label: 'Tasks', value: stats?.tasks || 0, color: 'text-violet-600' },
                { label: 'Posts', value: stats?.posts || 0, color: 'text-amber-600' },
              ].map(s => (
                <Card key={s.label} className="surface-card">
                  <CardContent className="pt-5 pb-4 px-5">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{s.label}</p>
                    <p className={`text-3xl font-bold ${s.color} mt-1`}>{s.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="surface-card">
                <CardContent className="pt-5 pb-4 px-5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-3">Invitations</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-foreground">{stats?.invitations || 0}</span>
                    <span className="text-sm text-muted-foreground">total</span>
                  </div>
                  <p className="text-sm text-amber-600 mt-1">{stats?.pending_invitations || 0} pending</p>
                </CardContent>
              </Card>
              <Card className="surface-card">
                <CardContent className="pt-5 pb-4 px-5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-3">Promo Codes</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-foreground">{promoCodes.length}</span>
                    <span className="text-sm text-muted-foreground">active codes</span>
                  </div>
                  <p className="text-sm text-cyan-600 mt-1">{promoCodes.reduce((sum, c) => sum + (c.times_used || 0), 0)} total redemptions</p>
                </CardContent>
              </Card>
            </div>

            {/* Vibe Check funnel tile */}
            <div className="mt-6">
              <Card className="surface-card border-2 border-emerald-100 dark:border-emerald-900/40" data-testid="vibe-check-funnel-tile">
                <CardContent className="pt-5 pb-4 px-5">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 uppercase tracking-wide flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" /> Vibe Check Funnel
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">Public Care Profile quiz starts → completions</p>
                    </div>
                    <div className="flex items-end gap-6">
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Starts</p>
                        <span className="text-2xl font-bold text-foreground" data-testid="vibe-check-starts">{stats?.quiz_starts ?? 0}</span>
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Completions</p>
                        <span className="text-2xl font-bold text-emerald-600" data-testid="vibe-check-completions">{stats?.quiz_completions ?? 0}</span>
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Conv %</p>
                        <span className="text-2xl font-bold text-cyan-600" data-testid="vibe-check-conv">
                          {stats?.quiz_conversion_pct == null ? '—' : `${stats.quiz_conversion_pct}%`}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Investor Demo Panel */}
            <Card className="surface-card border-2 border-purple-100 dark:border-purple-900/40 mt-6" data-testid="demo-panel">
              <CardContent className="pt-5 pb-4 px-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wide flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" /> Investor Demo
                    </p>
                    <p className="text-sm font-semibold text-foreground mt-1">demo@circleupsquad.com · Elena{"'"}s Care Circle</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Sarah Chen coordinating for Elena — 6 members, 6 tasks, 5 events, ready to show.</p>
                  </div>
                  <SeedDemoButton />
                </div>
              </CardContent>
            </Card>

            <div className="mt-6">
              <Card className="surface-card">
                <CardContent className="pt-5 pb-4 px-5">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Email System</p>
                      <p className="text-sm text-muted-foreground mt-1">Sends a live test through Resend and reports exactly what happens.</p>
                    </div>
                    <Button onClick={handleEmailCheck} disabled={checkingEmail} size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="email-health-btn">
                      {checkingEmail ? 'Testing...' : 'Test email system'}
                    </Button>
                  </div>
                  {emailCheck && (
                    <div className={`mt-3 rounded-xl p-3 text-sm ${emailCheck.test_send_status === 'sent' ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300' : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300'}`} data-testid="email-health-result">
                      <p className="font-semibold">Status: {emailCheck.test_send_status}</p>
                      {emailCheck.test_send_error && <p className="mt-1 break-all">Error: {emailCheck.test_send_error}</p>}
                      <p className="mt-1 text-xs opacity-80">
                        API key: {emailCheck.resend_key_present ? `present (${emailCheck.resend_key_prefix})` : 'MISSING'} · Sender: {emailCheck.sender_email || 'not set'}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <h3 className="text-sm font-semibold text-foreground mt-8 mb-3">Recent Users</h3>
            <div className="space-y-2">
              {users.slice(0, 8).map(u => (
                <div key={u.id} className="surface-card rounded-lg px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">{u.name || 'No name'}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.role === 'patient' ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>
                      {u.role || 'user'}
                    </span>
                    {u.is_admin && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-medium">Admin</span>}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* USERS TAB */}
          <TabsContent value="users">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search by name or email..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="admin-user-search" />
              </div>
              <button
                onClick={() => setShowStranded(!showStranded)}
                className={`text-xs font-semibold px-3 py-2 rounded-full border transition-colors ${showStranded ? 'bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' : 'border-border text-muted-foreground hover:text-foreground'}`}
                data-testid="stranded-filter-btn"
              >
                Not in a circle ({strandedCount})
              </button>
              <p className="text-sm text-muted-foreground">{filteredUsers.length} users</p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-3 text-xs text-muted-foreground font-medium uppercase">Name</th>
                    <th className="text-left py-3 px-3 text-xs text-muted-foreground font-medium uppercase">Email</th>
                    <th className="text-left py-3 px-3 text-xs text-muted-foreground font-medium uppercase">Role</th>
                    <th className="text-left py-3 px-3 text-xs text-muted-foreground font-medium uppercase">Signup Code</th>
                    <th className="text-left py-3 px-3 text-xs text-muted-foreground font-medium uppercase">Joined</th>
                    <th className="text-right py-3 px-3 text-xs text-muted-foreground font-medium uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(u => (
                    <tr key={u.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => { setSelectedUserId(u.id); setActivityDialogOpen(true); }}>
                      <td className="py-3 px-3 text-foreground font-medium">{u.name || '—'}</td>
                      <td className="py-3 px-3 text-muted-foreground">{u.email}</td>
                      <td className="py-3 px-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.role === 'patient' ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>
                          {u.role || 'user'}
                        </span>
                        {u.circle_count === 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 ml-1" data-testid={`no-circle-badge-${u.id}`}>No circle</span>
                        )}
                      </td>
                      <td className="py-3 px-3" data-testid={`signup-code-${u.id}`}>
                        {u.signup_code ? <span className="font-mono text-xs font-bold text-cyan-700 dark:text-cyan-300 bg-cyan-50 dark:bg-cyan-900/20 px-2 py-0.5 rounded">{u.signup_code}</span> : <span className="text-muted-foreground text-xs">—</span>}
                      </td>
                      <td className="py-3 px-3 text-muted-foreground text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                      <td className="py-3 px-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" className="text-cyan-600 hover:text-cyan-800" onClick={() => { setSelectedUserId(u.id); setActivityDialogOpen(true); }} data-testid={`view-user-${u.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-800" onClick={() => { setEmailUser(u); setEmailDialogOpen(true); }} data-testid={`email-user-${u.id}`}>
                            <Mail className="w-4 h-4" />
                          </Button>
                          {!u.is_admin && (
                            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDeleteUser(u.id, u.email)} data-testid={`delete-user-${u.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* CIRCLES TAB */}
          <TabsContent value="circles">
            <p className="text-sm text-muted-foreground mb-4">{circles.length} circles</p>
            <div className="space-y-3">
              {circles.map(c => (
                <CircleAdminCard key={c.id} circle={c} onDelete={handleDeleteCircle} />
              ))}
              {circles.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No circles yet</p>
              )}
            </div>
          </TabsContent>

          {/* CODES TAB */}
          <TabsContent value="codes">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">{promoCodes.length} promo codes</p>
              <CreateCodeDialog
                open={newCodeDialog}
                onOpenChange={setNewCodeDialog}
                code={newCode}
                setCode={setNewCode}
                onSubmit={handleCreateCode}
                loading={createLoading}
              />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-3 text-xs text-muted-foreground font-medium uppercase">Code</th>
                    <th className="text-left py-3 px-3 text-xs text-muted-foreground font-medium uppercase">Plan</th>
                    <th className="text-left py-3 px-3 text-xs text-muted-foreground font-medium uppercase">Uses</th>
                    <th className="text-left py-3 px-3 text-xs text-muted-foreground font-medium uppercase">Status</th>
                    <th className="text-right py-3 px-3 text-xs text-muted-foreground font-medium uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {promoCodes.map(c => (
                    <tr key={c.id || c.code} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-3 px-3 font-mono font-bold text-foreground">{c.code}</td>
                      <td className="py-3 px-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getPlanBadgeClass(c.plan)}`}>
                          {getPlanLabel(c.plan)}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-muted-foreground">{c.times_used || 0}{c.max_uses ? ` / ${c.max_uses}` : ''}</td>
                      <td className="py-3 px-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
                          {c.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => copyCode(c.code)}>
                          <Copy className="w-4 h-4 text-muted-foreground" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* EMAILS TAB */}
          <TabsContent value="emails">
            <EmailsPanel />
          </TabsContent>
          <TabsContent value="beta">
            <BetaInquiriesPanel />
          </TabsContent>
        </Tabs>
      </main>
      <UserActivityDialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen} userId={selectedUserId} />
      <EmailUserDialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen} user={emailUser} />
    </div>
  );
};

const CircleAdminCard = ({ circle, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [members, setMembers] = useState([]);
  const [addEmail, setAddEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [nudging, setNudging] = useState(null);
  const [inspectorOpen, setInspectorOpen] = useState(false);

  const handleNudge = async (m) => {
    if (!m.user?.id) return;
    setNudging(m.user.id);
    try {
      const res = await axios.post(`${API}/admin/circles/${circle.id}/members/${m.user.id}/welcome-nudge`);
      toast.success(res.data.message);
      setMembers(prev => prev.map(x => x.user?.id === m.user.id ? { ...x, welcome_nudge_sent_at: res.data.sent_at } : x));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send nudge'); }
    setNudging(null);
  };

  const fetchMembers = async () => {
    setLoadingMembers(true);
    try {
      const res = await axios.get(`${API}/admin/circles/${circle.id}/all-members`);
      setMembers(res.data.members);
    } catch (e) { toast.error('Could not load members'); }
    setLoadingMembers(false);
  };

  const handleExpand = () => {
    if (!expanded) fetchMembers();
    setExpanded(!expanded);
  };

  const handleAddMember = async () => {
    if (!addEmail.trim()) return;
    setAdding(true);
    try {
      const res = await axios.post(`${API}/admin/circles/${circle.id}/add-member`, { email: addEmail.trim() });
      toast.success(res.data.message);
      setAddEmail('');
      fetchMembers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add member'); }
    setAdding(false);
  };

  return (
    <Card className="surface-card">
      <CardContent className="pt-4 pb-4 px-5">
        <div className="flex items-center justify-between">
          <button onClick={handleExpand} className="flex-1 text-left flex items-center gap-3" data-testid={`expand-circle-${circle.id}`}>
            <div>
              <p className="text-sm font-semibold text-foreground">{circle.name}</p>
              <p className="text-xs text-muted-foreground">
                Code: <span className="font-mono font-bold">{circle.invite_code || circle.code || '—'}</span>
                {' · '}Created {new Date(circle.created_at).toLocaleDateString()}
              </p>
            </div>
          </button>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xl font-bold text-cyan-600">{circle.member_count}</p>
              <p className="text-[10px] text-muted-foreground">members</p>
              {circle.joins_this_week > 0 && (
                <p className="text-[10px] font-semibold text-emerald-600" data-testid={`joins-this-week-${circle.id}`}>+{circle.joins_this_week} this week</p>
              )}
            </div>
            <button onClick={handleExpand} className="text-muted-foreground hover:text-foreground transition-colors">
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            <Button variant="ghost" size="icon" onClick={() => setInspectorOpen(true)} title="Inspect (read-only)" data-testid={`inspect-circle-${circle.id}`}>
              <Eye className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onDelete(circle.id, circle.name)} className="text-destructive hover:bg-destructive/10" data-testid={`delete-circle-${circle.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <CircleInspectorDialog open={inspectorOpen} onOpenChange={setInspectorOpen} circleId={circle.id} circleName={circle.name} />

        {expanded && (
          <div className="mt-4 pt-4 border-t border-border/50 space-y-3">
            {/* Add member form */}
            <div className="flex gap-2">
              <Input
                placeholder="Add member by email..."
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                className="flex-1 text-sm"
                data-testid={`add-member-email-${circle.id}`}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddMember(); }}
              />
              <Button onClick={handleAddMember} disabled={adding || !addEmail.trim()} size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid={`add-member-btn-${circle.id}`}>
                <UserPlus className="w-4 h-4 mr-1" /> {adding ? '...' : 'Add'}
              </Button>
            </div>

            {/* Members list */}
            {loadingMembers ? (
              <p className="text-xs text-muted-foreground text-center py-2">Loading members...</p>
            ) : members.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-2">No members found</p>
            ) : (
              <div className="space-y-1.5">
                {members.map((m, i) => (
                  <div key={m.user?.id || `m-${i}`} className="flex items-center gap-2.5 py-1.5 px-2 rounded-lg hover:bg-muted/30">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                      {m.user?.name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{m.user?.name || 'Unknown'}</p>
                      <p className="text-[10px] text-muted-foreground">{m.user?.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {m.joined_via_code && (
                        <span
                          className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${m.code_recorded ? 'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-300' : 'bg-muted/60 text-muted-foreground'}`}
                          title={m.code_recorded ? 'Code used at signup/join' : 'Implied from circle code (joined before tracking)'}
                          data-testid={`member-code-${m.user?.id}`}
                        >
                          {m.joined_via_code}{!m.code_recorded && '*'}
                        </span>
                      )}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        m.role === 'patient' ? 'bg-cyan-100 text-cyan-700' : 'bg-slate-100 text-slate-600'
                      }`}>{m.role}</span>
                      <span className="text-[10px] text-muted-foreground/50">{m.collection}</span>
                      <button
                        onClick={() => handleNudge(m)}
                        disabled={nudging === m.user?.id}
                        title={m.welcome_nudge_sent_at ? `Welcome nudge sent ${new Date(m.welcome_nudge_sent_at).toLocaleDateString()} — send again` : 'Send welcome nudge email'}
                        className={`p-1.5 rounded-lg transition-colors ${m.welcome_nudge_sent_at ? 'text-emerald-600 hover:bg-emerald-50' : 'text-muted-foreground hover:text-cyan-600 hover:bg-cyan-50'}`}
                        data-testid={`welcome-nudge-${m.user?.id}`}
                      >
                        {m.welcome_nudge_sent_at ? <Check className="w-3.5 h-3.5" /> : <Send className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SeedDemoButton = () => {
  const [busy, setBusy] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const DEMO_TEXT = "CircleUpSquad demo login:\nURL: https://carecoord.emergent.host\nEmail: demo@circleupsquad.com\nPassword: DemoAccess2026!";

  const handleSeed = async () => {
    if (!window.confirm('This will reset the investor demo (demo@circleupsquad.com + Elena\'s Care Circle). Continue?')) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/seed-demo`);
      setLastRun(new Date().toLocaleTimeString());
      if (res.data.status === 'ok') {
        toast.success('Demo data reset. Login ready: demo@circleupsquad.com');
      } else {
        toast.error('Seed script errored — check backend logs');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not reset demo'); }
    setBusy(false);
  };

  const handleCopyLogin = async () => {
    try {
      await navigator.clipboard.writeText(DEMO_TEXT);
      toast.success('Demo login copied — paste it anywhere');
    } catch {
      // Fallback for browsers that block clipboard on non-HTTPS or older engines
      const ta = document.createElement('textarea');
      ta.value = DEMO_TEXT; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); toast.success('Demo login copied'); } catch { toast.error('Copy failed — long-press to copy manually'); }
      document.body.removeChild(ta);
    }
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex gap-2">
        <Button variant="outline" onClick={handleCopyLogin} data-testid="copy-demo-login-btn" className="border-purple-300 text-purple-700 hover:bg-purple-50">
          <Copy className="w-4 h-4 mr-2" /> Copy Login
        </Button>
        <Button onClick={handleSeed} disabled={busy} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="seed-demo-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${busy ? 'animate-spin' : ''}`} /> {busy ? 'Seeding...' : 'Reset Demo Data'}
        </Button>
      </div>
      {lastRun && <p className="text-[10px] text-muted-foreground">Last reset: {lastRun}</p>}
    </div>
  );
};


export default AdminPage;
